import React, { useMemo } from "react";
import Icon from "./icons";
import { sanitizeAnswerHtml } from "../../../lib/artemis/sanitize";
import { timeLabel } from "../../../lib/artemis/present";
import {
  alertActionLabel, alertExtras, changeExtras, channelsLabel, focusLabel, leadDaysLabel, statusTag, upcomingRows, warningExtras
} from "../../../lib/artemis/alerts";

// Artemis alerts on web (WEB-7145): an alert in the alerts conversation, Artemis's note about a push or
// text that couldn't go, and the card an alert tool's step returns in any chat.

const escapeHtml = text => String(text || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

// The message's Html, or its Text as paragraphs when there is none.
const useAlertHtml = message => useMemo(() => sanitizeAnswerHtml(message.html
  || String(message.text || '').split(/\n{2,}/).map(p => `<p>${escapeHtml(p)}</p>`).join('')), [message.html, message.text])

// A change watch's changes (WEB-7167): Artemis's summary, then each change grouped by what it is, old → new.
const ChangeList = ({ title, change }) => (
  <div className="chat-answer artemis-answer artemis-alert-body artemis-alert-changes">
    {title && <p><strong>{title}</strong></p>}
    {change.summary && <p>{change.summary}</p>}
    {change.groups.map(g => (
      <div key={g.category} className="artemis-alert-change-group">
        <span className="artemis-eyebrow">{g.title}</span>
        {g.rows.map(r => (
          <div key={r.key} className="artemis-alert-change-row">
            <span className="artemis-alert-change-label">{r.label === 'Booklet' ? (r.kind === 'gone' ? 'No longer used' : 'New booklet') : r.label}</span>
            <span className="artemis-alert-change-values">
              {r.kind === 'changed' && <><span className="artemis-alert-change-was">{r.before}</span><span aria-hidden="true"> → </span><span className="artemis-alert-change-now">{r.after}</span></>}
              {r.kind === 'new' && <><span className="artemis-hunt-badge">New</span> <span className="artemis-alert-change-now">{r.after}</span></>}
              {r.kind === 'gone' && <><span className="artemis-hunt-badge artemis-badge-off">Removed</span> <span className="artemis-alert-change-was">{r.before}</span></>}
            </span>
          </div>
        ))}
      </div>
    ))}
  </div>
)

// An alert in the alerts conversation (AuthorType "alert"): what, when, "Estimated" while the date is
// an estimate, and Apply when the state has a page for it. A change watch's alert lists its changes; a
// unit warning says what kind and when, with its source. highlighted is the one an alert link opened.
// The data-message-id is what an alert link scrolls to.
export const AlertMessage = ({ message, highlighted, onManage, now }) => {
  const html = useAlertHtml(message)
  if (message.notice) {
    // Why a push or text didn't go (sms:not_verified, sms:opted_out, push:no_device): a plain note from Artemis.
    return (
      <div className={`chat-section artemis-alert-notice ${highlighted ? 'artemis-alert-highlight' : ''}`} data-message-id={message.messageId}>
        <span className="artemis-avatar artemis-avatar-ai" aria-hidden="true"><Icon name="sparkle" size={13} /></span>
        <div className="chat-answer artemis-answer" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    )
  }
  const alert = message.alert || {}
  const extras = alertExtras(alert)
  const change = changeExtras(alert)
  const warning = alert.Kind === 'change' ? warningExtras(alert.Payload) : null
  return (
    <section className={`chat-section artemis-alert-card ${highlighted ? 'artemis-alert-highlight' : ''}`} data-message-id={message.messageId}
      aria-label={`Alert: ${alert.Title || 'Alert'}`}>
      <div className="artemis-alert-head">
        <Icon name="bell" size={15} />
        <span className="artemis-alert-kind">Alert</span>
        {extras && extras.date && (
          <span className="artemis-alert-date">
            <Icon name="calendar" size={13} />{extras.label ? `${extras.label} · ` : ''}{extras.date}
          </span>
        )}
        {extras && extras.estimate && <span className="artemis-hunt-badge artemis-badge-estimate">Estimated</span>}
        {extras && extras.change && <span className="artemis-hunt-badge">{extras.change}</span>}
        {change && <span className="artemis-hunt-badge">What changed</span>}
        {warning && <span className="artemis-hunt-badge artemis-badge-warning">{warning.label}</span>}
        {warning && warning.date && <span className="artemis-alert-date"><Icon name="calendar" size={13} />As of {warning.date}</span>}
        <span className="artemis-flex" />
        {message.at && <span className="artemis-alert-time">{timeLabel(message.at, now)}</span>}
      </div>
      {change
        ? <ChangeList title={alert.Title} change={change} />
        : <div className="chat-answer artemis-answer artemis-alert-body" dangerouslySetInnerHTML={{ __html: html }} />}
      {((extras && extras.applyUrl) || (warning && warning.url) || (onManage && alert.AlertId)) && (
        <div className="artemis-alert-actions">
          {warning && warning.url && (
            <a className="artemis-small-button" href={warning.url} target="_blank" rel="noopener noreferrer">
              {warning.source ? `Source: ${warning.source}` : 'Source'}<Icon name="external" size={13} />
            </a>
          )}
          {extras && extras.applyUrl && (
            <a className="artemis-small-button artemis-small-button-go" href={extras.applyUrl} target="_blank" rel="noopener noreferrer">
              Apply<Icon name="external" size={13} />
            </a>
          )}
          {onManage && alert.AlertId && <button type="button" className="artemis-small-button" onClick={() => onManage(alert.AlertId)}>Manage alert</button>}
        </div>
      )}
    </section>
  )
}

// One alert's line: title, what it watches, and a tag when it isn't running.
const AlertLine = ({ alert }) => (
  <div className="artemis-card-section">
    <span className="artemis-hunt-name">
      {alert.Title || 'Alert'}
      {statusTag(alert.Status) && <span className="artemis-hunt-badge artemis-badge-off">{statusTag(alert.Status)}</span>}
    </span>
    <span className="artemis-hunt-sub">
      {alert.Description || (alert.AlertKind === 'change' ? `Watching: ${focusLabel(alert.Focus, !!alert.Unit)}` : leadDaysLabel(alert.LeadDays))}
    </span>
  </div>
)

// The card an alert tool's step returns (create_alert, update_alert, delete_alert, get_alerts):
// what changed, the reminders a new alert will send, and a link to manage alerts.
export const SavedAlertCard = ({ card, onManage }) => {
  if (card.action === 'listed') {
    return (
      <section className="artemis-hunt-card artemis-card-tone-map" aria-label="Your alerts">
        <div className="artemis-hunt-card-header">
          <Icon name="bell" size={15} />
          <span className="artemis-hunt-card-title">{card.alerts.length ? `Your alerts · ${card.alerts.length}` : 'No alerts yet'}</span>
        </div>
        {card.alerts.slice(0, 6).map(a => <AlertLine key={a.Id} alert={a} />)}
        {onManage && (
          <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule">
            <span className="artemis-flex" />
            <button type="button" className="artemis-small-button" onClick={() => onManage(null)}>Manage alerts</button>
          </div>
        )}
      </section>
    )
  }
  const alert = card.alert
  const deleted = card.action === 'deleted'
  const upcoming = upcomingRows(alert.Upcoming)
  return (
    <section className="artemis-hunt-card artemis-card-tone-map" aria-label={`${alertActionLabel(card.action)}: ${alert.Title || ''}`}>
      <div className="artemis-hunt-card-header">
        <Icon name="bell" size={15} />
        <span className="artemis-hunt-card-title">{alertActionLabel(card.action)}</span>
      </div>
      <div className="artemis-card-section">
        <span className="artemis-hunt-name">{alert.Title || 'Alert'}</span>
        {!deleted && alert.Channels && <span className="artemis-hunt-sub">To: {channelsLabel(alert.Channels)}</span>}
      </div>
      {card.action === 'created' && alert.AlertKind === 'change' && (
        <p className="artemis-muted artemis-card-pad">
          Watching: {focusLabel(alert.Focus, !!alert.Unit)}. Artemis checks once a day
          {alert.Unit && (!alert.Focus || alert.Focus.length === 0 || alert.Focus.includes('warnings')) ? ', and for fire and closure warnings every 6 hours' : ''}.
        </p>
      )}
      {card.action === 'created' && alert.AlertKind !== 'change' && (
        upcoming.length > 0 ? (
          <div className="artemis-card-section artemis-alert-upcoming">
            <span className="artemis-eyebrow">Reminders</span>
            {upcoming.map(u => (
              <div key={u.key} className="artemis-alert-upcoming-row">
                <span className="artemis-flex">{u.lead}{u.label ? ` · ${u.label}` : ''}</span>
                <span className="artemis-alert-upcoming-date">{u.date}{u.estimate ? ' (est.)' : ''}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="artemis-muted artemis-card-pad">The dates aren’t out yet. Artemis reminds you once they are.</p>
        )
      )}
      {!deleted && onManage && (
        <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule">
          <span className="artemis-flex" />
          <button type="button" className="artemis-small-button" onClick={() => onManage(alert.Id)}>Manage alert</button>
        </div>
      )}
    </section>
  )
}
